/*
 * src/features/scope_module/utils/strategyPack.js
 * Builds the four overlays and the single 10×8 LANDSCAPE poster,
 * then rasterizes to a screen-friendly PNG (auto scales to viewport).
 */
import {
  computeGridFromImage,
  imageToDataUrl,
  generateCoverageGridSVG,
  generateOptimizedRouteSVG,
  generateConfidenceHalosSVG,
  generateHesitationMapSVG,
  assembleStrategyPosterLandscapeSVG,
  svgToDataUri
} from './strategySvgs';

function toBlob(str, type='image/svg+xml;charset=utf-8') { return new Blob([str], { type }); }
function saveBlob(blob, filename) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// Rasterize an SVG string to PNG at a target width (height preserves aspect)
export async function rasterizeSvgToPng(svgString, targetWidth = 1600, filename = 'poster.png') {
  const img = new Image();
  img.decoding = 'async';
  img.loading = 'eager';
  img.src = svgToDataUri(svgString);

  await new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(new Error('Failed to load assembled SVG'));
  });

  const aspect = img.height > 0 ? (img.width / img.height) : (5/4); // landscape fallback
  const W = Math.round(targetWidth);
  const H = Math.round(W / aspect);

  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, 0, 0, W, H);

  const blob = await new Promise(res => c.toBlob(res, 'image/png', 0.92));
  if (!blob) throw new Error('PNG encoding failed');
  saveBlob(blob, filename);
}

export function buildStrategyOverlays({
  imageWidth, imageHeight,
  trimmedPath, trimmedFeatures,
  grid = { cols: 10, rows: 8 },
  baseImage, clarityImage, qualityImage
}) {
  const claritySrc = clarityImage || qualityImage || null;
  const clarityGrid = claritySrc
    ? computeGridFromImage(claritySrc, imageWidth, imageHeight, grid.cols, grid.rows)
    : null;
  const bgUrl = baseImage ? imageToDataUrl(baseImage, imageWidth, imageHeight) : null;

  const coverageSvg = generateCoverageGridSVG({
    imageWidth, imageHeight, path: trimmedPath, grid, clarityGrid, bgImageDataUrl: bgUrl, showLegend: true, topN: 5
  });

  const routeSvg = generateOptimizedRouteSVG({
    imageWidth, imageHeight, path: trimmedPath, features: trimmedFeatures, grid, clarityGrid, bgImageDataUrl: bgUrl
  });

  const halosSvg = generateConfidenceHalosSVG({
    imageWidth, imageHeight, path: trimmedPath, features: trimmedFeatures, grid, clarityGrid, bgImageDataUrl: bgUrl,
    radiusPx: 28
  });

  const hesitationSvg = generateHesitationMapSVG({
    imageWidth, imageHeight, path: trimmedPath, features: trimmedFeatures, grid, clarityGrid, bgImageDataUrl: bgUrl
  });

  return { coverageSvg, routeSvg, halosSvg, hesitationSvg };
}

export function buildStrategyPosterSvgLandscape({
  overlays, // { coverageSvg, routeSvg, halosSvg, hesitationSvg }
  posterWidth = 3000,
  posterHeight = 2400,
  header,
  subHeader
}) {
  return assembleStrategyPosterLandscapeSVG({
    posterWidth,
    posterHeight,
    coverageSvg: overlays.coverageSvg,
    routeSvg: overlays.routeSvg,
    halosSvg: overlays.halosSvg,
    hesitationSvg: overlays.hesitationSvg,
    header,
    subHeader
  });
}

// Auto pick a screen-friendly width so 1:1 CSS pixels ≈ screen pixels
function pickTargetWidth() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1600;
  return Math.max(1000, Math.min(1600, Math.floor(vw - 64)));
}

export async function downloadStrategyPosterPng(posterSvg, baseName = 'strategy') {
  const tgt = pickTargetWidth();
  await rasterizeSvgToPng(posterSvg, tgt, `${baseName}-poster-10x8.png`);
}
